import React from 'react';
import { Modal } from '../../components/Modal';
import { SummaryRow, BoolIcon } from '../../components/SummaryRow';
import { TagPill, TagLabels } from '../../components/TagPill';
import { useReplayExport } from '../../hooks/useExports';
import { broadcastBasicSearch, isSuperAdmin } from '../../services/angular-bridge';
import { fieldContains } from '../../utilities/searchSyntax';
import type { Export, ExportTag } from '../../hooks/useExports';

interface Props {
  projectId: string;
  exportObj: Export;
  onClose: () => void;
}

export function ExportInfoModal({ projectId, exportObj: exp, onClose }: Props) {
  const replay = useReplayExport(projectId);
  const canReplay = isSuperAdmin();

  const endorsementLabel = (arr?: ExportTag[]) => arr?.[0]?.value ?? arr?.[0]?.name ?? '';
  const searchTag = (tagId: string) => {
    onClose();
    broadcastBasicSearch(fieldContains('systemTags', tagId));
  };

  return (
    <Modal
      title={exp.name}
      icon="fas fa-share"
      onClose={onClose}
      large
      testId="export-list-info-modal"
      bodyStyle={{ paddingLeft: 32, paddingRight: 32 }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <div>
            {canReplay && (
              <button
                className="btn btn-danger"
                data-testid="export-list-info-replay-btn"
                onClick={() => replay.mutate(exp.id)}
                disabled={replay.isPending}
              >
                Replay Export
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {exp.directDownloadPath && (
              <a
                className="btn btn-primary"
                href={exp.directDownloadPath}
                data-testid="export-list-info-download-link"
                download
              >
                <i className="fas fa-download" aria-hidden="true" /> Download
              </a>
            )}
            <button className="btn btn-default" data-testid="export-list-info-close-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {exp.id && <SummaryRow label="ID">{exp.id}</SummaryRow>}
        {exp.createdAtString && <SummaryRow label="Created">{exp.createdAtString}</SummaryRow>}
        <SummaryRow label="Name">{exp.name}</SummaryRow>
        {exp.account?.email && <SummaryRow label="Creator">{exp.account.email}</SummaryRow>}
        <SummaryRow label="Color Setting">{exp.colorSetting}</SummaryRow>
        <SummaryRow label="Resources with Processing Exceptions">
          <BoolIcon value={exp.includeResourcesWithProcessingExceptions} />
        </SummaryRow>
        <SummaryRow label="Resources with Zero Pages">
          <BoolIcon value={exp.includeResourcesWithZeroPages} />
        </SummaryRow>
        <SummaryRow label="Duplicate Resources">
          <BoolIcon value={exp.includeDuplicateResources} />
        </SummaryRow>
        <SummaryRow label="Redacted Resources">
          <BoolIcon value={exp.includeRedactedResources} />
        </SummaryRow>
        <SummaryRow label="Keep Original Page Orientation">
          <BoolIcon value={exp.keepPageOrientation} />
        </SummaryRow>
        <SummaryRow label="Collections">
          <div className="badge-list">
            {exp.collectionNames?.map(c => (
              <span key={c} className="label label-default">{c}</span>
            ))}
          </div>
        </SummaryRow>
        {exp.tags?.and && exp.tags?.and?.length > 0 && (
          <SummaryRow label="And Tags"><TagLabels tags={exp.tags.and} /></SummaryRow>
        )}
        {exp.tags?.or && exp.tags?.or?.length > 0 && (
          <SummaryRow label="Or Tags"><TagLabels tags={exp.tags.or} /></SummaryRow>
        )}
        {exp.tags?.not && exp.tags?.not?.length > 0 && (
          <SummaryRow label="Not Tags"><TagLabels tags={exp.tags.not} /></SummaryRow>
        )}
        {exp.containerExportTag && (
          <SummaryRow label="Container Export Tag">
            <TagPill tag={exp.containerExportTag} />
          </SummaryRow>
        )}
        <SummaryRow label="Load File Formats">
          <div className="badge-list">
            {exp.loadFileFormatNames?.map(f => (
              <span key={f} className="label label-default">{f}</span>
            ))}
          </div>
        </SummaryRow>
        {exp.outputTypes?.length > 0 && (
          <SummaryRow label="Output Types">
            <div className="badge-list">
              {exp.outputTypes.map(t => (
                <span key={t} className="label label-default">{t}</span>
              ))}
            </div>
          </SummaryRow>
        )}
        {exp.outputTypes?.length > 0 && (
          <SummaryRow label="Placeholder Settings">
            <div className="badge-list">
              <span className="label label-default">Missing Images</span>
              {exp.spreadsheetPlaceholder && (
                <span className="label label-default">Spreadsheets</span>
              )}
              {exp.presentationPlaceholder && (
                <span className="label label-default">Presentations</span>
              )}
              {exp.tagPlaceholder?.name && (
                <span className="label label-default">
                  Documents Tagged with {exp.tagPlaceholder.name}
                </span>
              )}
            </div>
          </SummaryRow>
        )}
        <SummaryRow label="Prefix">{exp.prefix}</SummaryRow>
        <SummaryRow label="Start value">{exp.startValue}</SummaryRow>
        <SummaryRow label="Zero padding">{exp.zeroPaddingLength}</SummaryRow>
        <SummaryRow label="Files per directory">{exp.filesPerDirectory}</SummaryRow>
        {exp.hadExportException && (
          <SummaryRow label="Exception">
            <span className="text-danger">{exp.exportException}</span>
          </SummaryRow>
        )}
        {exp.exportTag && (
          <SummaryRow label="Export Tag">
            <TagPill tag={exp.exportTag} onClick={() => searchTag(exp.exportTag!.id)} />
          </SummaryRow>
        )}
        <SummaryRow label="Virus Placeholder Text">{exp.virusPlaceholderText}</SummaryRow>
        <SummaryRow label="Native Format Placeholder Text">{exp.nativeFormatPlaceholderText}</SummaryRow>
        <SummaryRow label="Failed to Image Placeholder">{exp.failedToImagePlaceholderText}</SummaryRow>
        <SummaryRow label="South West Endorsement">{endorsementLabel(exp.southWestEndorsement)}</SummaryRow>
        <SummaryRow label="South Endorsement">{endorsementLabel(exp.southEndorsement)}</SummaryRow>
        <SummaryRow label="South East Endorsement">{endorsementLabel(exp.southEastEndorsement)}</SummaryRow>
      </div>
    </Modal>
  );
}
